import * as React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import IconSearch from "../../../assets/svg/search-icon-grey.svg";
import toast from "react-hot-toast";
import config from "../../../config";
import axiosInstant from "../../../api";
import { useUser } from "../../../hooks/useUser";
import ListView from "./components/ListView";
import DownloadIcon from "@mui/icons-material/Download";
import { isInternal } from "../../../utils";
import ListingFilters from "../../../components/ListingFilters";
import DownloadCsv from "./DownloadCsv";
import { DotLottleLoader, LoadMoreBtn, PageDivider } from "../../../components/Loader";
import SortControls from "../../../components/SortControls/SortControls";
import { AddExperttoShortlist } from "../../admin/Shortlists/AddExperttoShortlist";
import { Button, Checkbox, Typography } from "@mui/material";
import EditSelectedModal from "./components/EditSelectedModal";
import { postCreateAutoLoginRedirect } from "../../../api/endpoints";
import { IconPlus, IconUser } from "../../../utils/icons";
import { NoDataView } from "../../../components/EmptyPanel/NoDataView";
import { useCommonParams } from '../../../hooks/useCommonParams';
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { debounce } from "lodash";
import { usePagination } from "../../../hooks/usePagination";
import { useReorderMode } from "../../../hooks/useReorderMode";

const PAGE_SIZE = 20;

export default function BrowsePros({
  addExpertToShortlist,
  isSharePath = false,
  showDialog,
  setShowDialog,
  showShortlistFilter,
  isExpertPage,
  isShortlistPage,
  queryParams,
  shortlistData = null,
}) {
  const { role } = useUser();
  const {
    searchParams,
    setSearchParams,
    page,
    setPage,
    view,
    setView,
  } = useCommonParams({ defaultView: "list" });

  const sortParam = searchParams.get("sort") || "";
  const hasShortlist = isShortlistPage || searchParams.get("shortlist");
  const sortType = React.useMemo(() => {
    if (!sortParam) {
      return {
        key: hasShortlist ? "custom" : "dateCreated",
        isAscending: hasShortlist ? true : false,
      };
    }
    const isAscending = !sortParam.startsWith("-");
    const key = sortParam.replace(/^-/, "");
    return { key, isAscending };
  }, [sortParam, hasShortlist]);

  const setSortType = ({ key, isAscending }) => {
    setSearchParams((p) => {
      const val = `${isAscending ? "" : "-"}${key}`;
      if (!val) p.delete("sort");
      else p.set("sort", val);
      return p;
    });
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isAnythingSelected, setIsAnythingSelected] = useState(false);
  const [selectedPro, setSelectedPro] = useState(null);
  const debouncedSetParamsRef = useRef(null);
  const [localSearchInput, setLocalSearchInput] = useState(searchParams.get("search") || "");
  const [expertToDelete, setExpertToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shortlists, setShortlists] = useState([]);
  const shortlistsFetchedRef = React.useRef(false);

  const shortlistId = useMemo(() => {
    const parsed = parseInt(searchParams.get("shortlist"), 10);
    return isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  const fetchFn = useCallback(
    async (pageNum) => {
      try {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete("view");
        currentParams.delete("page");
        const paramsObj = Object.fromEntries(currentParams.entries());

        const response = await axiosInstant.get("/users/pros", {
          params: {
            ...paramsObj,
            page: pageNum,
            limit: PAGE_SIZE,
          },
        });
        const { rows, count } = response.data;
        return { rows: rows ?? [], count: count ?? 0, error: null };
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") {
          return { rows: [], count: 0, error: null };
        }
        return { rows: [], count: 0, error: e?.message || "Failed to load experts" };
      }
    },
    [searchParams],
  );

  const {
    items: paginatedPros,
    totalCount: total,
    loading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    error,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["page", "view"],
    },
  });

  // Reorder mode works on its own full, unpaginated copy of the shortlist —
  // it never touches the pagination state, the filters, or the URL.
  const reorderMode = useReorderMode({
    fetchAll: useCallback(async () => {
      const response = await axiosInstant.get("/users/pros", {
        params: { shortlist: shortlistId, sorting: true },
      });
      return response.data.rows ?? [];
    }, [shortlistId]),
    saveOrder: useCallback(
      async (orderedPros) => {
        await axiosInstant.patch(`/shortlists/${shortlistId}/experts/order`, {
          order: orderedPros.map((pro) => pro.userid),
        });
      },
      [shortlistId],
    ),
    onSaved: useCallback(() => {
      // Show the saved order: switching the sort param to "custom" triggers a
      // refetch through the deep-link effect; if it's already custom, refetch
      // the current page directly.
      if (sortType.key === "custom") {
        initLoad(page);
      } else {
        setSortType({ key: "custom", isAscending: true });
      }
      // eslint-disable-next-line
    }, [sortType.key, initLoad, page]),
  });

  const pros = reorderMode.active ? reorderMode.items : paginatedPros;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchInput(value);
    debouncedSetParamsRef.current?.(value);
  };

  useEffect(() => {
    debouncedSetParamsRef.current = debounce((value) => {
      setSearchParams((p) => {
        if (!value) p.delete("search");
        else p.set("search", value);
        return p;
      });
    }, 150);

    return () => {
      debouncedSetParamsRef.current?.cancel();
      debouncedSetParamsRef.current = null;
    };
  }, [setSearchParams]);

  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    setLocalSearchInput(urlSearch);
  }, [searchParams]);

  useEffect(() => {
    if (shortlistsFetchedRef.current) return;
    shortlistsFetchedRef.current = true;
    axiosInstant
      .get("/shortlists?all=true", {})
      .then((response) => setShortlists(response.data))
      .catch(() => {});
  }, []);

  const handleSortTypeChange = (newValue) => {
    let isAscending = sortType.isAscending;
    let key = newValue;
    setSearchParams((p) => {
      let sort = "" + (isAscending ? "" : "-") + key;
      if (sort == "") p.delete("sort");
      else p.set("sort", sort);
      return p;
    });
  };

  const handleOrderChange = (asc) => {
    let key = sortType.key;
    setSearchParams((p) => {
      let sort = "" + (asc ? "" : "-") + key;
      if (sort == "") p.delete("sort");
      else p.set("sort", sort);
      return p;
    });
  };

  const isShortlistEmpty = React.useMemo(() => {
    const isShortlistResultsEmpty = isShortlistPage && pros.length === 0;
    const searchParamKeysLength = Array.from(searchParams.keys()).filter(
      (key) => key !== "shortlist"
    ).length;
    return isShortlistResultsEmpty && searchParamKeysLength === 0;
  }, [isShortlistPage, pros, searchParams]);

  const isSelectedAll = React.useMemo(() => {
    return selectedIds.length === pros.length && pros.length > 0;
  }, [selectedIds, pros]);

  const handleSelectAll = () => {
    if (selectedIds.length === pros.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pros.map((pro) => pro.userid));
    }
  };

  const removeFromShortlist = (id) => {
    const currentShortlistId = shortlistId || shortlistData?.id || Number(searchParams?.get('shortlist'));

    if (!currentShortlistId || isNaN(currentShortlistId)) {
      toast.error("Unable to remove expert. Shortlist ID is missing.");
      return;
    }

    setSelectedIds((prevIds) => prevIds.filter((selectedId) => selectedId !== id));

    axiosInstant
      .delete(`/shortlists/${currentShortlistId}/experts/${id}`)
      .then(() => {
        toast.success("Expert removed from shortlist.");
        initLoad(page);
      })
      .catch((error) => {
        console.error("Error removing expert:", error);
        toast.error("Failed to remove expert. Try again.");
      });
  };

  const deleteUser = (id) => {
    const expert = pros.find((pro) => String(pro.userid) === String(id));
    if (expert) {
      setExpertToDelete(expert);
    } else {
      setExpertToDelete({ userid: id });
    }
  };

  const confirmDeleteUser = async () => {
    if (!expertToDelete?.userid || shortlistId) return;

    const expertId = expertToDelete.userid;
    setIsDeleting(true);

    try {
      await axiosInstant.delete(`${config.API_URL}/proteam/users/${expertId}`);
      toast.success("Expert has been successfully removed.");
      setExpertToDelete(null);
      initLoad(page);
    } catch (error) {
      console.error("Error deleting expert:", error);
      toast.error("Something went wrong. Try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const createAutoLoginUrl = async (pro) => {
    try {
      let res = await axiosInstant.post(postCreateAutoLoginRedirect(), {
        userid: pro.userid.toString(),
      });
      let redirectUrl = res.data.redirectUrl;
      navigator.clipboard.writeText(redirectUrl);
      toast.success("URL copied to clipboard");
    } catch (error) {}
  };

  const handleOpenModal = (pro) => {
    setSelectedPro(pro);
    setModalOpen(true);
  };

  const addToShortlist = (newPro) => {
    handleOpenModal(newPro);
  };

  const handleSubmit = async (values) => {
    if (!selectedIds.length) {
      toast.error("No users selected.");
      return;
    }

    const updates = { ...values };
    if (updates.shortlist) {
      updates.shortlist = Number(updates.shortlist);
    }

    try {
      const response = await axiosInstant.patch("/users/bulk-update", {
        userIds: selectedIds,
        updates,
      });

      if (response.status === 200) {
        toast.success("Users updated successfully.");
        setIsEditModalOpen(false);
        initLoad(page);
      } else {
        toast.error("Update failed. Try again.");
      }
    } catch (error) {
      console.error("Error updating users:", error);
      toast.error("Error updating users.");
    }
  };

  return (
    <>
      <div className="mx-auto">
        {isInternal(role) && !isSharePath && (
          <>
            <div className="pt-8 pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-[91px]">
                <div className="flex justify-center">
                  <div className="w-full relative">
                    <img
                      className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                      src={IconSearch}
                    />
                    <input
                      type="text"
                      className="w-full sm:w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                      id="search-experts"
                      placeholder="Search"
                      value={localSearchInput || ""}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>
                <div className="flex gap-2 items-center justify-end flex-wrap">
                  {!isSharePath && isAnythingSelected && (
                    <Button onClick={() => setIsEditModalOpen(true)} size="small" variant="contained">
                      Edit Selected
                    </Button>
                  )}

                  <ListingFilters
                    shouldShowFilter={isInternal(role)}
                    showBrandFilter
                    showShortlistFilter={showShortlistFilter}
                    onSearchUpdate={() => {}}
                    type="experts"
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                  />
                  <SortControls
                    sortKey={sortType.key}
                    isAscending={sortType.isAscending}
                    onSortKeyChange={handleSortTypeChange}
                    onDirectionChange={handleOrderChange}
                    options={[
                      ...((isShortlistPage || searchParams.get("shortlist")) ? [{ value: "custom", label: "Custom" }] : []),
                      { value: "dateCreated", label: "Created Date" },
                      { value: "dateOfBirth", label: "Age" },
                      { value: "displayName", label: "Name" },
                      { value: "email_address", label: "Email" },
                      { value: "proScore", label: "Score" },
                      { value: "tier", label: "Tier" },
                      { value: "years_experience", label: "Experience" },
                      { value: "total_reach", label: "Reach" },
                      { value: "total_income", label: "Income" },
                      { value: "total_projects", label: "Projects" },
                      { value: "total_clients", label: "Clients" },
                    ]}
                    selectWidth={200}
                  />
                </div>
              </div>
            </div>

            <PageDivider
              count={total}
              label="Experts"
              className="mb-5 mt-5"
              canLoadPrevious={canLoadPrevious}
              hasMore={hasMore}
              onLoadPrevious={loadPrevious}
            />

            {false && isInternal(role) && (
              <Button className="ml-10 downloadBtn" variant="contained">
                Download PDF <DownloadIcon />
              </Button>
            )}
          </>
        )}

        {(loading || reorderMode.loading) && pros.length === 0 ? (
          <DotLottleLoader />
        ) : (
          <>
            {!isSharePath && Boolean(pros.length) && (
              <div className="flex gap-4 items-start py-4">
                <Checkbox
                  checked={isSelectedAll}
                  onChange={handleSelectAll}
                  className="!p-0 rounded-lg"
                  sx={{
                    "& .MuiSvgIcon-root": { fill: "var(--muted)" },
                    "&.Mui-checked .MuiSvgIcon-root": { fill: "var(--accent)" },
                  }}
                />
                <div className="flex flex-col gap-4">
                  {isSelectedAll ? "Unselect" : "Select All"}
                </div>

                {isShortlistPage && isInternal(role) && !reorderMode.active && pros.length > 1 && (
                  <Button
                    variant="outlined"
                    onClick={reorderMode.start}
                    sx={{
                      textTransform: "none",
                      ml: "auto",
                    }}>
                      Reorder
                  </Button>
                )}

                {isShortlistPage && isInternal(role) && reorderMode.active && (
                  <div className="flex gap-2 ml-auto">
                    <Button
                      variant="outlined"
                      onClick={reorderMode.cancel}
                      disabled={reorderMode.saving}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={reorderMode.save}
                      disabled={reorderMode.saving || reorderMode.loading}
                      sx={{ textTransform: "none" }}
                    >
                      {reorderMode.saving ? "Saving..." : "Save Order"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {canLoadPrevious && !reorderMode.active && <div ref={topSentinelRef} style={{ height: 1 }} />}
            {loadingPrevious && (
              <div className="flex justify-center py-2">
                <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
              </div>
            )}

            {pros.length > 0 && view === "list" && (
              <ListView
                pros={pros}
                deleteUser={deleteUser}
                addToShortlist={addToShortlist}
                removeFromShortlist={removeFromShortlist}
                isExpertPage={isExpertPage}
                isShortlistPage={isShortlistPage}
                setIsAnythingSelected={setIsAnythingSelected}
                selectAll={selectAll}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                createAutoLoginUrl={createAutoLoginUrl}
                isSharePath={isSharePath}
                sorting={reorderMode.active}
                onReorder={isShortlistPage && isInternal(role) ? reorderMode.reorder : undefined}
              />
            )}

            {pros.length > 0 && hasMore && !reorderMode.active && (
              <LoadMoreBtn onClick={loadMore} loading={loading} />
            )}

            {pros.length === 0 && isShortlistPage && (
              <div>
                <NoDataView
                  Icon={IconUser}
                  text=""
                  subText={
                    isShortlistEmpty
                      ? "Add your first expert to get started!"
                      : "No experts available. Try clearing or updating your filters to expand your search."
                  }
                >
                  {addExpertToShortlist && isShortlistEmpty && (
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ display: "flex", gap: 2, alignItems: "center" }}
                      onClick={addExpertToShortlist}
                    >
                      <IconPlus />
                      <Typography variant="body2">Add Expert</Typography>
                    </Button>
                  )}
                </NoDataView>
              </div>
            )}

            {pros.length === 0 && !isShortlistPage && (
              <NoDataView
                Icon={IconUser}
                text="No experts found"
                subText="Your search does not match any existing experts. "
              />
            )}
          </>
        )}
      </div>

      <AddExperttoShortlist
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedExpert={selectedPro}
      />

      <EditSelectedModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        shortlists={shortlists}
        handleSubmit={handleSubmit}
      />

      {isInternal(role) && (
        <DownloadCsv
          showDialog={showDialog}
          setShowDialog={setShowDialog}
          filters={searchParams}
        />
      )}

      {expertToDelete && (
        <ConfirmationDialog
          open={true}
          onClose={() => setExpertToDelete(null)}
          onConfirm={confirmDeleteUser}
          title="Delete Expert"
          description="Are you sure you want to delete this expert?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          disabled={isDeleting}
        />
      )}
    </>
  );
}

BrowsePros.propTypes = {
  addExpertToShortlist: () => {},
};
